import React, { useState, useEffect, useMemo } from 'react';
import {
  BarChart, Bar, Cell, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
  LineChart, Line, Legend, PieChart, Pie
} from 'recharts';
import {
  Zap, DollarSign, Hash, BarChart3, Trash2, RefreshCw, Cpu,
  TrendingUp, TrendingDown, Database, Activity, Server, Clock,
  Search, Layers, Brain, MessageSquare, FileText, Target, Save, Settings
} from 'lucide-react';
import api from '../../lib/api';
import { toast } from 'sonner';

const COLORS = ['#2563eb', '#7c3aed', '#059669', '#d97706', '#dc2626', '#0891b2', '#be185d', '#4f46e5', '#9333ea', '#ea580c'];

// ─── HELPERS ───
const formatTokens = (n) => {
  if (!n) return '0';
  if (n >= 1_000_000) return (n / 1_000_000).toFixed(2) + 'M';
  if (n >= 1_000) return (n / 1_000).toFixed(1) + 'K';
  return n.toLocaleString();
};

const formatCost = (n) => {
  if (n === undefined || n === null) return '$0.00';
  return '$' + n.toFixed(4);
};

// ─── MAIN COMPONENT ───
export default function TokenDiagnostics() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [rates, setRates] = useState({
    'deepseek-v4-flash': { input: 0.14, output: 0.28, cache: 0.014 },
    'deepseek-v4': { input: 0.14, output: 0.28, cache: 0.014 },
    'deepseek-r1': { input: 0.55, output: 2.19, cache: 0.055 },
  });
  const [activeModel, setActiveModel] = useState('deepseek-v4-flash');
  const [savingRates, setSavingRates] = useState(false);

  useEffect(() => { loadData(); loadRates(); }, []);

  const loadData = async () => {
    setLoading(true);
    try {
      const res = await api.get('/api/admin/token-diagnostics');
      setData(res.data);
    } catch (err) {
      toast.error('Failed to load token diagnostics');
    } finally {
      setLoading(false);
    }
  };

  const loadRates = async () => {
    try {
      const res = await api.get('/api/admin/settings');
      const saved = res.data?.token_pricing || {};
      if (Object.keys(saved).length > 0) {
        setRates(prev => ({ ...prev, ...saved }));
      }
    } catch {}
  };

  const handleSaveRates = async () => {
    setSavingRates(true);
    try {
      await api.post('/api/admin/settings', { key: 'token_pricing', value: JSON.stringify(rates) });
      toast.success('Pricing rates saved');
    } catch {
      toast.error('Failed to save rates');
    } finally {
      setSavingRates(false);
    }
  };

  const handleClear = async () => {
    try {
      await api.post('/api/admin/token-diagnostics/clear');
      toast.success('Token logs cleared');
      loadData();
    } catch {
      toast.error('Failed to clear logs');
    }
  };

  if (loading) return <div style={{ padding: 24, color: 'var(--text-secondary)' }}>Loading...</div>;
  if (!data) return null;

  const { summary, per_lead, per_search, cost_estimate, cache_stats, daily_trend } = data;
  const modelRates = rates[activeModel] || { input: 0.14, output: 0.28, cache: 0.014 };

  // Computed values
  const totalInputTokens = summary?.total_input_tokens || summary?.total_tokens || 0;
  const totalOutputTokens = summary?.total_output_tokens || 0;
  const totalCacheSaved = cache_stats?.total_saved_tokens || summary?.cache_saved_tokens || 0;
  const cacheHitRate = totalInputTokens > 0 ? Math.round((totalCacheSaved / totalInputTokens) * 100) : 0;
  const totalCost = cost_estimate?.total || 0;

  const sectionData = summary?.sections ? Object.entries(summary.sections)
    .filter(([name]) => !name.startsWith('_'))
    .map(([name, s]) => ({
      name: name.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase()),
      tokens: s.tokens || 0,
      calls: s.calls || 0,
      cache_hits: s.cache_hits || 0,
      cache_misses: s.cache_misses || 0,
      input_tokens: s.input_tokens || 0,
      output_tokens: s.output_tokens || 0,
    })) : [];

  const pieData = sectionData.map(s => ({ name: s.name, value: s.tokens }));

  const dailyData = daily_trend || [];

  return (
    <div style={{ padding: 'clamp(16px, 3vw, 24px)', maxWidth: 1400 }}>
      {/* ── HEADER ── */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 24, flexWrap: 'wrap', gap: 12 }}>
        <div>
          <h2 style={{ color: 'var(--text-primary)', fontSize: '1.25rem', fontWeight: 700, margin: '0 0 4px', display: 'flex', alignItems: 'center', gap: 8 }}>
            <Cpu size={22} color="var(--accent-primary)" /> Token Usage & AI Cost Analytics
          </h2>
          <p style={{ color: 'var(--text-tertiary)', fontSize: 13, margin: 0 }}>Real-time token consumption, cache performance, and cost tracking</p>
        </div>
        <div style={{ display: 'flex', gap: 8 }}>
          <button onClick={loadData} style={btnStyle('var(--info)')}><RefreshCw size={14} /> Refresh</button>
          <button onClick={handleClear} style={btnStyle('var(--error)')}><Trash2 size={14} /> Clear Logs</button>
        </div>
      </div>

      {/* ── COST CONFIGURATION ── */}
      <div style={{ ...cardStyle, marginBottom: 24 }}>
        <h3 style={headingStyle}><Settings size={14} /> Token Pricing Configuration (per 1M tokens)</h3>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: 16 }}>
          {Object.entries(rates).map(([model, modelRates]) => (
            <div key={model} style={{
              padding: 16, borderRadius: 12,
              background: activeModel === model ? 'var(--accent-glow)' : 'var(--bg-tertiary)',
              border: activeModel === model ? '1px solid var(--accent-primary)40' : '1px solid var(--border-primary)',
              cursor: 'pointer', transition: 'all 0.15s ease',
            }} onClick={() => setActiveModel(model)}>
              <div style={{ fontSize: 12, fontWeight: 700, color: 'var(--text-primary)', marginBottom: 12, textTransform: 'uppercase', letterSpacing: '0.04em' }}>
                {model.replace(/-/g, ' ')}
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 8 }}>
                <div>
                  <div style={{ fontSize: 9, color: 'var(--text-tertiary)', textTransform: 'uppercase', marginBottom: 2 }}>Input $/1M</div>
                  <input type="number" step="0.01" min="0" value={modelRates.input}
                    onChange={e => setRates(prev => ({ ...prev, [model]: { ...prev[model], input: parseFloat(e.target.value) || 0 } }))}
                    onClick={e => e.stopPropagation()}
                    style={rateInputStyle} />
                </div>
                <div>
                  <div style={{ fontSize: 9, color: 'var(--text-tertiary)', textTransform: 'uppercase', marginBottom: 2 }}>Output $/1M</div>
                  <input type="number" step="0.01" min="0" value={modelRates.output}
                    onChange={e => setRates(prev => ({ ...prev, [model]: { ...prev[model], output: parseFloat(e.target.value) || 0 } }))}
                    onClick={e => e.stopPropagation()}
                    style={rateInputStyle} />
                </div>
                <div>
                  <div style={{ fontSize: 9, color: 'var(--text-tertiary)', textTransform: 'uppercase', marginBottom: 2 }}>Cache $/1M</div>
                  <input type="number" step="0.001" min="0" value={modelRates.cache}
                    onChange={e => setRates(prev => ({ ...prev, [model]: { ...prev[model], cache: parseFloat(e.target.value) || 0 } }))}
                    onClick={e => e.stopPropagation()}
                    style={rateInputStyle} />
                </div>
              </div>
            </div>
          ))}
        </div>
        <button onClick={handleSaveRates} disabled={savingRates} style={{
          ...btnStyle('var(--success)'), marginTop: 16,
          opacity: savingRates ? 0.6 : 1,
        }}>
          <Save size={14} /> {savingRates ? 'Saving...' : 'Save Pricing Rates'}
        </button>
      </div>

      {/* ── KPI CARDS ── */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(155px, 1fr))', gap: 12, marginBottom: 24 }}>
        <KPICard icon={<Hash size={20} />} label="Total Input Tokens" value={formatTokens(totalInputTokens)} sub={`Output: ${formatTokens(totalOutputTokens)}`} color="#2563eb" />
        <KPICard icon={<Database size={20} />} label="Cache Hit Rate" value={`${cacheHitRate}%`} sub={`${formatTokens(totalCacheSaved)} tokens saved`} color="#7c3aed" />
        <KPICard icon={<Activity size={20} />} label="Total API Calls" value={(summary?.total_calls || 0).toLocaleString()} sub={`${sectionData.length} sections`} color="#059669" />
        <KPICard icon={<DollarSign size={20} />} label="Est. Total Cost" value={formatCost(totalCost)} sub={`@ $${modelRates.input}/M input`} color="#d97706" />
        <KPICard icon={<Zap size={20} />} label="Avg Tokens/Lead" value={formatTokens(summary?.avg_tokens_per_lead || 0)} color="#dc2626" />
        <KPICard icon={<Server size={20} />} label="Avg Tokens/Search" value={formatTokens(summary?.avg_tokens_per_search || 0)} color="#0891b2" />
      </div>

      {/* ── CHARTS ROW 1: Area Chart + Pie Chart ── */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(400px, 1fr))', gap: 20, marginBottom: 24 }}>
        <div style={cardStyle}>
          <h3 style={headingStyle}><BarChart3 size={14} /> Token Usage by Section</h3>
          {sectionData.length > 0 ? (
            <ResponsiveContainer width="100%" height={300}>
              <BarChart data={sectionData}>
                <CartesianGrid strokeDasharray="3 3" stroke="var(--border-primary)" />
                <XAxis dataKey="name" tick={{ fontSize: 10, fill: 'var(--text-secondary)' }} angle={-20} textAnchor="end" height={60} />
                <YAxis tick={{ fontSize: 11, fill: 'var(--text-secondary)' }} tickFormatter={formatTokens} />
                <Tooltip contentStyle={tooltipStyle} formatter={(val) => [formatTokens(val), 'Tokens']} />
                <Bar dataKey="tokens" radius={[6, 6, 0, 0]}>
                  {sectionData.map((_, i) => <Cell key={i} fill={COLORS[i % COLORS.length]} />)}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          ) : <EmptyState text="No data yet. Run a search to populate." />}
        </div>
        <div style={cardStyle}>
          <h3 style={headingStyle}><Layers size={14} /> Input vs Output Distribution</h3>
          {sectionData.length > 0 ? (
            <ResponsiveContainer width="100%" height={300}>
              <PieChart>
                <Pie data={[
                  { name: 'Input Tokens', value: totalInputTokens },
                  { name: 'Output Tokens', value: totalOutputTokens },
                  { name: 'Cache Saved', value: totalCacheSaved },
                ]} cx="50%" cy="50%" innerRadius={60} outerRadius={110} paddingAngle={5} dataKey="value">
                  <Cell fill="#2563eb" />
                  <Cell fill="#7c3aed" />
                  <Cell fill="#059669" />
                </Pie>
                <Tooltip contentStyle={tooltipStyle} formatter={(val) => [formatTokens(val), '']} />
                <Legend />
              </PieChart>
            </ResponsiveContainer>
          ) : <EmptyState text="No data yet." />}
        </div>
      </div>

      {/* ── CACHE PERFORMANCE ── */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: 20, marginBottom: 24 }}>
        <div style={cardStyle}>
          <h3 style={headingStyle}><Database size={14} /> Cache Performance by Section</h3>
          {sectionData.filter(s => (s.cache_hits || 0) + (s.cache_misses || 0) > 0).length > 0 ? (
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr style={{ borderBottom: '1px solid var(--border-primary)' }}>
                  <th style={thStyle}>Section</th>
                  <th style={thStyle}>Hits</th>
                  <th style={thStyle}>Misses</th>
                  <th style={thStyle}>Hit Rate</th>
                </tr>
              </thead>
              <tbody>
                {sectionData.filter(s => (s.cache_hits || 0) + (s.cache_misses || 0) > 0).map((s, i) => {
                  const total = (s.cache_hits || 0) + (s.cache_misses || 0);
                  const rate = total > 0 ? Math.round((s.cache_hits / total) * 100) : 0;
                  return (
                    <tr key={i} style={{ borderBottom: '1px solid var(--border-primary)' }}>
                      <td style={tdStyle}>{s.name}</td>
                      <td style={{ ...tdStyle, color: 'var(--success)', fontWeight: 600 }}>{s.cache_hits || 0}</td>
                      <td style={{ ...tdStyle, color: 'var(--error)' }}>{s.cache_misses || 0}</td>
                      <td style={tdStyle}>
                        <span style={{ padding: '2px 8px', borderRadius: 20, fontSize: 11, fontWeight: 600, background: rate >= 70 ? 'var(--success-bg)' : rate >= 40 ? 'var(--warning-bg)' : 'var(--error-bg)', color: rate >= 70 ? 'var(--success)' : rate >= 40 ? 'var(--warning)' : 'var(--error)' }}>
                          {rate}%
                        </span>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          ) : <EmptyState text="No cache data yet. DeepSeek caching requires repeated identical system prompts." />}
        </div>
        <div style={cardStyle}>
          <h3 style={headingStyle}><TrendingUp size={14} /> Daily Cost Trend (7 days)</h3>
          {dailyData.length > 0 ? (
            <ResponsiveContainer width="100%" height={220}>
              <LineChart data={dailyData}>
                <CartesianGrid strokeDasharray="3 3" stroke="var(--border-primary)" />
                <XAxis dataKey="date" tick={{ fontSize: 11, fill: 'var(--text-secondary)' }} />
                <YAxis tick={{ fontSize: 11, fill: 'var(--text-secondary)' }} tickFormatter={(v) => '$' + v.toFixed(2)} />
                <Tooltip contentStyle={tooltipStyle} formatter={(val) => ['$' + Number(val).toFixed(4), 'Cost']} />
                <Line type="monotone" dataKey="cost" stroke="#2563eb" strokeWidth={2} dot={{ r: 4 }} />
              </LineChart>
            </ResponsiveContainer>
          ) : <EmptyState text="Insufficient data for daily trend. Data accumulates over time." />}
        </div>
      </div>

      {/* ── SECTION DETAIL TABLE ── */}
      <div style={{ ...cardStyle, marginBottom: 24 }}>
        <h3 style={headingStyle}><Brain size={14} /> Section Detail</h3>
        {sectionData.length > 0 ? (
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr style={{ borderBottom: '1px solid var(--border-primary)' }}>
                <th style={thStyle}>Section</th>
                <th style={thStyle}>Input Tokens</th>
                <th style={thStyle}>Output Tokens</th>
                <th style={thStyle}>Total</th>
                <th style={thStyle}>Calls</th>
                <th style={thStyle}>Avg/ Call</th>
                <th style={thStyle}>Est. Cost</th>
                <th style={thStyle}>% of Total</th>
              </tr>
            </thead>
            <tbody>
              {sectionData.map((s, i) => (
                <tr key={i} style={{ borderBottom: '1px solid var(--border-primary)' }}>
                  <td style={{ ...tdStyle, fontWeight: 600, color: COLORS[i % COLORS.length] }}>{s.name}</td>
                  <td style={tdStyle}>{formatTokens(s.input_tokens)}</td>
                  <td style={tdStyle}>{formatTokens(s.output_tokens)}</td>
                  <td style={{ ...tdStyle, fontWeight: 600 }}>{formatTokens(s.tokens)}</td>
                  <td style={tdStyle}>{s.calls.toLocaleString()}</td>
                  <td style={tdStyle}>{formatTokens(Math.round(s.tokens / Math.max(s.calls, 1)))}</td>
                  <td style={{ ...tdStyle, color: 'var(--warning)', fontWeight: 600 }}>
                    {formatCost((s.input_tokens / 1_000_000) * modelRates.input + (s.output_tokens / 1_000_000) * modelRates.output)}
                  </td>
                  <td style={tdStyle}>{totalInputTokens + totalOutputTokens > 0 ? Math.round((s.tokens / (totalInputTokens + totalOutputTokens)) * 100) : 0}%</td>
                </tr>
              ))}
              {/* Total row */}
              <tr style={{ borderTop: '2px solid var(--border-primary)', background: 'var(--bg-tertiary)' }}>
                <td style={{ ...tdStyle, fontWeight: 700 }}>TOTAL</td>
                <td style={{ ...tdStyle, fontWeight: 700 }}>{formatTokens(totalInputTokens)}</td>
                <td style={{ ...tdStyle, fontWeight: 700 }}>{formatTokens(totalOutputTokens)}</td>
                <td style={{ ...tdStyle, fontWeight: 700 }}>{formatTokens(totalInputTokens + totalOutputTokens)}</td>
                <td style={{ ...tdStyle, fontWeight: 700 }}>{(summary?.total_calls || 0).toLocaleString()}</td>
                <td style={tdStyle}>-</td>
                <td style={{ ...tdStyle, fontWeight: 700, color: 'var(--warning)' }}>{formatCost(totalCost)}</td>
                <td style={{ ...tdStyle, fontWeight: 700 }}>100%</td>
              </tr>
            </tbody>
          </table>
        ) : <EmptyState text="Run a search to see token breakdown by section." />}
      </div>

      {/* ── PER SEARCH BREAKDOWN ── */}
      <div style={cardStyle}>
        <h3 style={headingStyle}><Search size={14} /> Per Search Breakdown</h3>
        {per_search?.length > 0 ? (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            {per_search.map((search, i) => {
              const searchLeads = per_lead?.filter(l => {
                const leadSearchId = l.search_id || '';
                const currentSearchId = search.search_id || '';
                return leadSearchId === currentSearchId || currentSearchId === 'unknown';
              }) || [];
              const searchCost = searchLeads.reduce((sum, l) => {
                const sections = l.sections || {};
                const inputTokens = Object.values(sections).reduce((a, b) => a + b, 0) * 0.6;
                const outputTokens = Object.values(sections).reduce((a, b) => a + b, 0) * 0.4;
                return sum + (inputTokens / 1_000_000) * modelRates.input + (outputTokens / 1_000_000) * modelRates.output;
              }, 0);
              return (
                <details key={i} style={{
                  background: 'var(--bg-tertiary)', borderRadius: 12,
                  border: '1px solid var(--border-primary)', overflow: 'hidden',
                }}>
                  <summary style={{
                    padding: '14px 18px', cursor: 'pointer',
                    display: 'flex', alignItems: 'center', gap: 12,
                    fontSize: 13, fontWeight: 600, color: 'var(--text-primary)',
                    userSelect: 'none',
                  }}>
                    <span style={{ width: 8, height: 8, borderRadius: '50%', background: COLORS[i % COLORS.length], flexShrink: 0 }} />
                    <span style={{ flex: 1, fontSize: 12 }}>
                      {search.search_id === 'unknown' ? 'Background / Re-score' : (search.search_id?.length > 35 ? search.search_id.slice(0, 32) + '...' : search.search_id)}
                    </span>
                    <span style={{ fontSize: 12, color: 'var(--text-secondary)' }}>{search.lead_count} leads</span>
                    <span style={{ fontSize: 12, fontWeight: 700, color: COLORS[i % COLORS.length] }}>{formatTokens(search.tokens)} tokens</span>
                    <span style={{ fontSize: 11, color: 'var(--text-tertiary)' }}>~{formatTokens(search.tokens_per_lead)}/lead</span>
                    <span style={{ fontSize: 11, color: 'var(--warning)', fontWeight: 600 }}>{formatCost(searchCost)}</span>
                  </summary>
                  <div style={{ padding: '0 18px 14px' }}>
                    {/* Sections in this search */}
                    <table style={{ width: '100%', borderCollapse: 'collapse', marginBottom: 12 }}>
                      <thead>
                        <tr style={{ borderBottom: '1px solid var(--border-primary)' }}>
                          <th style={{ ...thStyle, fontSize: 10 }}>Section</th>
                          <th style={{ ...thStyle, fontSize: 10 }}>Tokens</th>
                          <th style={{ ...thStyle, fontSize: 10 }}>% of Search</th>
                        </tr>
                      </thead>
                      <tbody>
                        {Object.entries(search.sections || {}).sort((a, b) => b[1] - a[1]).map(([section, tokens], j) => (
                          <tr key={j} style={{ borderBottom: '1px solid var(--border-primary)' }}>
                            <td style={{ ...tdStyle, fontSize: 11 }}>{section.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase())}</td>
                            <td style={{ ...tdStyle, fontSize: 11, fontWeight: 600 }}>{formatTokens(tokens)}</td>
                            <td style={{ ...tdStyle, fontSize: 11 }}>{search.tokens > 0 ? Math.round((tokens / search.tokens) * 100) : 0}%</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>

                    {/* Per-Lead within this search */}
                    {searchLeads.length > 0 && (
                      <div>
                        <p style={{ fontSize: 10, color: 'var(--text-tertiary)', textTransform: 'uppercase', fontWeight: 600, marginBottom: 8 }}>
                          <Target size={10} /> Leads in this search ({searchLeads.length})
                        </p>
                        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                          <thead>
                            <tr style={{ borderBottom: '1px solid var(--border-primary)' }}>
                              <th style={{ ...thStyle, fontSize: 10 }}>Lead</th>
                              <th style={{ ...thStyle, fontSize: 10 }}>Tokens</th>
                              <th style={{ ...thStyle, fontSize: 10 }}>Est. Cost</th>
                              <th style={{ ...thStyle, fontSize: 10 }}>Sections</th>
                            </tr>
                          </thead>
                          <tbody>
                            {searchLeads.map((lead, k) => {
                              const leadSections = lead.sections || {};
                              const leadInput = Object.values(leadSections).reduce((a, b) => a + b, 0) * 0.6;
                              const leadOutput = Object.values(leadSections).reduce((a, b) => a + b, 0) * 0.4;
                              const leadCost = (leadInput / 1_000_000) * modelRates.input + (leadOutput / 1_000_000) * modelRates.output;
                              return (
                                <tr key={k} style={{ borderBottom: '1px solid var(--border-primary)' }}>
                                  <td style={{ ...tdStyle, fontSize: 11 }}>{lead.lead?.length > 30 ? lead.lead.slice(0, 27) + '...' : lead.lead}</td>
                                  <td style={{ ...tdStyle, fontSize: 11, fontWeight: 600 }}>{formatTokens(lead.tokens)}</td>
                                  <td style={{ ...tdStyle, fontSize: 10, color: 'var(--warning)', fontWeight: 600 }}>{formatCost(leadCost)}</td>
                                  <td style={{ ...tdStyle, fontSize: 10, color: 'var(--text-tertiary)' }}>
                                    {Object.entries(leadSections).slice(0, 3).map(([s, t]) => `${s.replace(/_/g, ' ')}: ${formatTokens(t)}`).join(', ')}
                                    {Object.keys(leadSections).length > 3 ? ` +${Object.keys(leadSections).length - 3} more` : ''}
                                  </td>
                                </tr>
                              );
                            })}
                          </tbody>
                        </table>
                      </div>
                    )}
                  </div>
                </details>
              );
            })}
          </div>
        ) : <EmptyState text="No search data yet. Run a search to see per-search breakdown." />}

      {/* ── SEARCH HISTORY WITH TOKEN BREAKDOWN ── */}
      <div style={{ ...cardStyle, marginBottom: 24 }}>
        <h3 style={headingStyle}><Search size={14} /> Search History with Token Breakdown</h3>
        {per_search?.length > 0 ? (
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: 700 }}>
              <thead>
                <tr style={{ borderBottom: '1px solid var(--border-primary)' }}>
                  <th style={thStyle}>Search Query / ID</th>
                  <th style={thStyle}>Date</th>
                  <th style={thStyle}>Leads</th>
                  <th style={thStyle}>Tokens</th>
                  <th style={thStyle}>Tokens/Lead</th>
                  <th style={thStyle}>Est. Cost</th>
                  <th style={thStyle}>Sections</th>
                </tr>
              </thead>
              <tbody>
                {per_search.map((search, i) => {
                  const searchCost = ((search.tokens * 0.6) / 1_000_000) * modelRates.input + ((search.tokens * 0.4) / 1_000_000) * modelRates.output;
                  const sectionList = Object.entries(search.sections || {}).sort((a, b) => b[1] - a[1]);
                  return (
                    <React.Fragment key={i}>
                      <tr style={{ borderBottom: '1px solid var(--border-primary)', cursor: 'pointer' }}
                        onClick={(e) => {
                          const detail = e.currentTarget.nextElementSibling;
                          if (detail) detail.style.display = detail.style.display === 'table-row' ? 'none' : 'table-row';
                        }}>
                        <td style={{ ...tdStyle, fontSize: 12, maxWidth: 300, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                          {search.search_id === 'unknown' ? 'Background / Re-score' : (search.search_id?.length > 50 ? search.search_id.slice(0, 47) + '...' : search.search_id)}
                        </td>
                        <td style={{ ...tdStyle, fontSize: 11, color: 'var(--text-tertiary)' }}>{search.date || ', '}</td>
                        <td style={{ ...tdStyle, fontSize: 12, fontWeight: 600 }}>{search.lead_count}</td>
                        <td style={{ ...tdStyle, fontSize: 12, fontWeight: 600, color: COLORS[i % COLORS.length] }}>{formatTokens(search.tokens)}</td>
                        <td style={{ ...tdStyle, fontSize: 11 }}>{formatTokens(search.tokens_per_lead)}</td>
                        <td style={{ ...tdStyle, fontSize: 11, color: 'var(--warning)', fontWeight: 600 }}>{formatCost(searchCost)}</td>
                        <td style={{ ...tdStyle, fontSize: 11, color: 'var(--text-tertiary)' }}>
                          {sectionList.slice(0, 2).map(([s]) => s.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase())).join(', ')}
                          {sectionList.length > 2 ? ` +${sectionList.length - 2}` : ''}
                        </td>
                      </tr>
                      {/* Expandable detail row */}
                      <tr style={{ display: 'none', background: 'var(--bg-primary)' }}>
                        <td colSpan={7} style={{ padding: '14px 18px' }}>
                          <div style={{ fontSize: 11, color: 'var(--text-secondary)', fontWeight: 600, marginBottom: 8 }}>Section Breakdown:</div>
                          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                            <thead>
                              <tr style={{ borderBottom: '1px solid var(--border-primary)' }}>
                                <th style={{ ...thStyle, fontSize: 10 }}>Section</th>
                                <th style={{ ...thStyle, fontSize: 10 }}>Tokens</th>
                                <th style={{ ...thStyle, fontSize: 10 }}>% of Search</th>
                                <th style={{ ...thStyle, fontSize: 10 }}>Est. Cost</th>
                              </tr>
                            </thead>
                            <tbody>
                              {sectionList.map(([section, tokens], j) => {
                                const secCost = ((tokens * 0.6) / 1_000_000) * modelRates.input + ((tokens * 0.4) / 1_000_000) * modelRates.output;
                                return (
                                  <tr key={j} style={{ borderBottom: '1px solid var(--border-primary)' }}>
                                    <td style={{ ...tdStyle, fontSize: 11 }}>{section.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase())}</td>
                                    <td style={{ ...tdStyle, fontSize: 11, fontWeight: 600 }}>{formatTokens(tokens)}</td>
                                    <td style={{ ...tdStyle, fontSize: 11 }}>{search.tokens > 0 ? Math.round((tokens / search.tokens) * 100) : 0}%</td>
                                    <td style={{ ...tdStyle, fontSize: 10, color: 'var(--warning)', fontWeight: 600 }}>{formatCost(secCost)}</td>
                                  </tr>
                                );
                              })}
                            </tbody>
                          </table>
                        </td>
                      </tr>
                    </React.Fragment>
                  );
                })}
              </tbody>
            </table>
          </div>
        ) : <EmptyState text="No search data yet. Run a search to see the breakdown." />}
      </div>

      {/* ── PER-LEAD TOKEN DETAIL (Deep Dive) ── */}
      <div style={cardStyle}>
        <h3 style={headingStyle}><Target size={14} /> Per-Lead Token Detail (Deep Dive)</h3>
        {per_lead?.length > 0 ? (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
            {per_lead.slice(0, 20).map((lead, i) => {
              const leadSections = lead.sections || {};
              const leadInput = Object.values(leadSections).reduce((a, b) => a + b, 0) * 0.6;
              const leadOutput = Object.values(leadSections).reduce((a, b) => a + b, 0) * 0.4;
              const leadCost = (leadInput / 1_000_000) * modelRates.input + (leadOutput / 1_000_000) * modelRates.output;
              const hasCache = lead.cache_saved_tokens > 0;
              return (
                <details key={i} style={{
                  background: 'var(--bg-tertiary)', borderRadius: 10,
                  border: '1px solid var(--border-primary)', overflow: 'hidden',
                }}>
                  <summary style={{
                    padding: '12px 16px', cursor: 'pointer',
                    display: 'flex', alignItems: 'center', gap: 10,
                    fontSize: 13, fontWeight: 600, color: 'var(--text-primary)',
                    userSelect: 'none',
                  }}>
                    <span style={{ width: 6, height: 6, borderRadius: '50%', background: COLORS[i % COLORS.length], flexShrink: 0 }} />
                    <span style={{ flex: 1, fontSize: 12 }}>
                      {lead.lead?.length > 35 ? lead.lead.slice(0, 32) + '...' : (lead.lead || `Lead #${i + 1}`)}
                    </span>
                    <span style={{ fontSize: 11, fontWeight: 700, color: COLORS[i % COLORS.length] }}>{formatTokens(lead.tokens)} tokens</span>
                    {hasCache && <span style={{ fontSize: 10, padding: '1px 8px', borderRadius: 10, background: 'var(--success-bg)', color: 'var(--success)', fontWeight: 600 }}>⚡ {formatTokens(lead.cache_saved_tokens)} saved</span>}
                    <span style={{ fontSize: 10, color: 'var(--warning)', fontWeight: 600 }}>{formatCost(leadCost)}</span>
                  </summary>
                  <div style={{ padding: '0 16px 12px' }}>
                    {/* Input/Output breakdown */}
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 8, marginBottom: 10 }}>
                      <div style={{ padding: 8, borderRadius: 6, background: 'var(--bg-primary)', textAlign: 'center' }}>
                        <div style={{ fontSize: 10, color: 'var(--text-tertiary)' }}>Input Tokens</div>
                        <div style={{ fontSize: 14, fontWeight: 700, color: '#2563eb' }}>{formatTokens(leadInput)}</div>
                      </div>
                      <div style={{ padding: 8, borderRadius: 6, background: 'var(--bg-primary)', textAlign: 'center' }}>
                        <div style={{ fontSize: 10, color: 'var(--text-tertiary)' }}>Output Tokens</div>
                        <div style={{ fontSize: 14, fontWeight: 700, color: '#7c3aed' }}>{formatTokens(leadOutput)}</div>
                      </div>
                      <div style={{ padding: 8, borderRadius: 6, background: 'var(--bg-primary)', textAlign: 'center' }}>
                        <div style={{ fontSize: 10, color: 'var(--text-tertiary)' }}>Cache Saved</div>
                        <div style={{ fontSize: 14, fontWeight: 700, color: hasCache ? 'var(--success)' : 'var(--text-tertiary)' }}>
                          {hasCache ? formatTokens(lead.cache_saved_tokens) : ', '}
                        </div>
                      </div>
                    </div>
                    {/* Per-section detail for this lead */}
                    {Object.keys(leadSections).length > 0 && (
                      <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                        <thead>
                          <tr style={{ borderBottom: '1px solid var(--border-primary)' }}>
                            <th style={{ ...thStyle, fontSize: 10 }}>Section</th>
                            <th style={{ ...thStyle, fontSize: 10 }}>Tokens</th>
                            <th style={{ ...thStyle, fontSize: 10 }}>Cache Hit</th>
                            <th style={{ ...thStyle, fontSize: 10 }}>Est. Cost</th>
                          </tr>
                        </thead>
                        <tbody>
                          {Object.entries(leadSections).sort((a, b) => b[1] - a[1]).map(([section, tokens], j) => {
                            const secCost = ((tokens * 0.6) / 1_000_000) * modelRates.input + ((tokens * 0.4) / 1_000_000) * modelRates.output;
                            return (
                              <tr key={j} style={{ borderBottom: '1px solid var(--border-primary)' }}>
                                <td style={{ ...tdStyle, fontSize: 11 }}>
                                  {section.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase())}
                                </td>
                                <td style={{ ...tdStyle, fontSize: 11, fontWeight: 600 }}>{formatTokens(tokens)}</td>
                                <td style={{ ...tdStyle, fontSize: 11 }}>
                                  {lead.cache_hit ? (
                                    <span style={{ color: 'var(--success)', fontWeight: 600 }}>✅ Hit</span>
                                  ) : (
                                    <span style={{ color: 'var(--text-tertiary)' }}>, </span>
                                  )}
                                </td>
                                <td style={{ ...tdStyle, fontSize: 10, color: 'var(--warning)', fontWeight: 600 }}>{formatCost(secCost)}</td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    )}
                  </div>
                </details>
              );
            })}
          </div>
        ) : (
          <div style={{ textAlign: 'center', padding: 30 }}>
            <p style={{ color: 'var(--text-tertiary)', fontSize: 13, margin: '0 0 8px' }}>
              <Brain size={24} style={{ marginBottom: 8, opacity: 0.5 }} />
            </p>
            <p style={{ color: 'var(--text-tertiary)', fontSize: 13, margin: 0 }}>
              No per-lead token data yet.
            </p>
            <p style={{ color: 'var(--text-tertiary)', fontSize: 11, margin: '4px 0 0' }}>
              Lead-level tracking requires passing <code style={{ background: 'var(--bg-tertiary)', padding: '2px 6px', borderRadius: 4 }}>lead_name</code> to <code style={{ background: 'var(--bg-tertiary)', padding: '2px 6px', borderRadius: 4 }}>log_prompt_tokens()</code>.
            </p>
          </div>
        )}
      </div>
      </div>
    </div>
  );
}

// ─── SUB-COMPONENTS ──────────────────────────────────────────────────

function KPICard({ icon, label, value, sub, color }) {
  return (
    <div style={{ background: 'var(--bg-secondary)', border: '1px solid var(--border-primary)', borderLeft: `3px solid ${color}`, borderRadius: 12, padding: 16 }}>
      <div style={{ color, marginBottom: 6 }}>{icon}</div>
      <div style={{ fontSize: '1.3rem', fontWeight: 800, color: 'var(--text-primary)' }}>{value}</div>
      <div style={{ fontSize: 11, color: 'var(--text-secondary)', marginTop: 2 }}>{label}</div>
      {sub && <div style={{ fontSize: 10, color: 'var(--text-tertiary)', marginTop: 2 }}>{sub}</div>}
    </div>
  );
}

function EmptyState({ text }) {
  return <div style={{ textAlign: 'center', padding: 40, color: 'var(--text-tertiary)', fontSize: 13 }}>{text}</div>;
}

// ─── STYLES ──────────────────────────────────────────────────────────

const btnStyle = (color) => ({
  display: 'inline-flex', alignItems: 'center', gap: 6,
  padding: '8px 14px', borderRadius: 8,
  border: `1px solid ${color}40`, background: 'transparent',
  color, cursor: 'pointer', fontSize: 12, fontWeight: 600,
});

const cardStyle = {
  background: 'var(--bg-secondary)', border: '1px solid var(--border-primary)',
  borderRadius: 16, padding: 20, boxShadow: 'var(--shadow-sm)',
};

const headingStyle = {
  color: 'var(--text-secondary)', fontSize: 13, fontWeight: 600,
  textTransform: 'uppercase', letterSpacing: '0.05em',
  margin: '0 0 16px', paddingBottom: 10, borderBottom: '1px solid var(--border-primary)',
  display: 'flex', alignItems: 'center', gap: 8,
};

const thStyle = {
  textAlign: 'left', padding: '10px 12px', fontSize: 11, fontWeight: 600,
  color: 'var(--text-tertiary)', textTransform: 'uppercase', letterSpacing: '0.04em',
};

const tdStyle = { padding: '10px 12px', fontSize: 13, color: 'var(--text-primary)' };

const rateInputStyle = {
  width: '100%', padding: '6px 8px', borderRadius: 6, boxSizing: 'border-box',
  border: '1px solid var(--border-primary)', background: 'var(--input-bg)',
  color: 'var(--text-primary)', fontSize: 12, fontWeight: 600, textAlign: 'center',
};

const tooltipStyle = {
  background: 'var(--bg-secondary)', border: '1px solid var(--border-primary)',
  borderRadius: 8, fontSize: 12,
};
